import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, Search, Download, Upload, Package, AlertTriangle, IndianRupee, Calendar, X, CheckCheck, ChevronDown, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { useContext } from "react";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { NotificationContext } from "../context/NotificationContext";
import { useExportSuccess } from "../context/ExportSuccessContext";
import { productAPI, inventoryAPI, categoryAPI, supplierAPI, purchaseAPI } from "../api/apiService";
import SmartHSNInput from "../components/SmartHSNInput";
import { storage } from "../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { uploadImage } from "../services/storageService";
import { resolveMediaUrl } from "../utils/resolveMediaUrl";
import { useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import EmptyState from "../components/EmptyState";

// Barcode format and value generator
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });
};

const generateBarcodeValue = (productName, category, variantName = null, formatStyle = 'GIO_PRD', symbology = 'CODE128') => {
  if (symbology === 'EAN13') {
    const prefix = "890";
    const randomPart = Math.floor(100000000 + Math.random() * 900000000);
    return `${prefix}${randomPart}`;
  }
  if (symbology === 'UPC') {
    const prefix = "0";
    const randomPart = Math.floor(1000000000 + Math.random() * 9000000000);
    return `${prefix}${randomPart}`;
  }

  const randomNum = Math.floor(10000 + Math.random() * 90000);
  const cleanProduct = (productName || 'PRD').split(' ')[0].replace(/[^a-zA-Z]/g, '').toUpperCase().substring(0, 3);
  const cleanCategory = (category || 'GEN').split(' ')[0].replace(/[^a-zA-Z]/g, '').toUpperCase().substring(0, 4);
  
  if (variantName) {
    const cleanVar = variantName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 4);
    return `VAR-${cleanVar || 'VAR'}-${String(Math.floor(100 + Math.random() * 900)).padStart(5, '0')}`;
  }

  switch (formatStyle) {
    case 'GIO_SEQ':
      const seqNum = Math.floor(1 + Math.random() * 999999);
      return `GIO${String(seqNum).padStart(6, '0')}`;
    case 'CAT_BASED':
      return `CAT-${cleanCategory}-${randomNum}`;
    case 'GIO_PRD':
    default:
      return `GIO-${cleanProduct || 'PRD'}-${randomNum}`;
  }
};

// Canvas drawing helper for labels (PNG/PDF/Print)
const drawCanvasLabel = (value, prodName, varName, price, symbology, callback) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = 300;
  canvas.height = 180;
  
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  ctx.fillStyle = '#111827';
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(prodName.toUpperCase(), canvas.width / 2, 24);
  
  if (varName) {
    ctx.fillStyle = '#4B5563';
    ctx.font = 'medium 10px sans-serif';
    ctx.fillText(`Variant: ${varName}`, canvas.width / 2, 38);
  }

  const drawBottomText = () => {
    if (price !== null && price !== undefined && price !== "") {
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Price: ₹${parseFloat(price).toFixed(2)}`, canvas.width / 2, varName ? 146 : 138);
    }
    
    ctx.fillStyle = '#9CA3AF';
    ctx.font = '8px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Spice Garden Restaurant', canvas.width / 2, canvas.height - 10);
    
    callback(canvas);
  };

  if (symbology === 'QR Code') {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent(value)}`;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      ctx.drawImage(image, (canvas.width - 90) / 2, varName ? 45 : 40);
      drawBottomText();
    };
    image.onerror = () => {
      ctx.fillStyle = '#EF4444';
      ctx.font = '10px sans-serif';
      ctx.fillText('[QR Code Image]', canvas.width / 2, 80);
      drawBottomText();
    };
    image.src = qrUrl;
  } else {
    const tempSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    try {
      JsBarcode(tempSvg, value, {
        format: ['CODE128', 'CODE39', 'EAN13', 'UPC'].includes(symbology) ? symbology : 'CODE128',
        width: 1.5,
        height: 50,
        displayValue: true,
        fontSize: 10,
        margin: 0
      });
      
      const svgString = new XMLSerializer().serializeToString(tempSvg);
      const svgBlob = new Blob([svgString], {type: 'image/svg+xml;charset=utf-8'});
      const URL = window.URL || window.webkitURL || window;
      const blobURL = URL.createObjectURL(svgBlob);
      
      const image = new Image();
      image.onload = () => {
        ctx.drawImage(image, (canvas.width - image.width) / 2 || 20, varName ? 45 : 40);
        URL.revokeObjectURL(blobURL);
        drawBottomText();
      };
      image.onerror = () => {
        ctx.fillStyle = '#EF4444';
        ctx.font = '10px sans-serif';
        ctx.fillText('[Barcode Rendering Failed]', canvas.width / 2, 80);
        drawBottomText();
      };
      image.src = blobURL;
    } catch (err) {
      console.error(err);
      ctx.fillStyle = '#EF4444';
      ctx.font = '10px sans-serif';
      ctx.fillText('[Invalid Barcode]', canvas.width / 2, 80);
      drawBottomText();
    }
  }
};

const downloadBarcodeAsPNG = (value, prodName, varName = null, price = null, symbology = 'CODE128') => {
  drawCanvasLabel(value, prodName, varName, price, symbology, (canvas) => {
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `barcode_${value}.png`;
    link.click();
  });
};

const downloadBarcodeAsPDF = (value, prodName, varName = null, price = null, symbology = 'CODE128') => {
  drawCanvasLabel(value, prodName, varName, price, symbology, (canvas) => {
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [85, 54]
    });
    pdf.addImage(imgData, 'PNG', 0, 0, 85, 54);
    pdf.save(`barcode_${value}.pdf`);
  });
};

const printBarcodeLabel = (value, prodName, varName = null, price = null, symbology = 'CODE128') => {
  drawCanvasLabel(value, prodName, varName, price, symbology, (canvas) => {
    const imgData = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Barcode Label</title>
            <style>
              body {
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                background-color: white;
              }
              img {
                max-width: 100%;
                height: auto;
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <img src="${imgData}" />
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  });
};

// Barcode Live Preview Component
function BarcodeRenderer({ value, format = "CODE128", height = 40, width = 1.5, displayValue = true }) {
  const elementRef = React.useRef(null);

  React.useEffect(() => {
    if (elementRef.current && value && format !== 'QR Code') {
      try {
        JsBarcode(elementRef.current, value, {
          format: ['CODE128', 'CODE39', 'EAN13', 'UPC'].includes(format) ? format : 'CODE128',
          lineColor: '#000',
          width: width,
          height: height,
          displayValue: displayValue,
          fontSize: 10,
          margin: 4
        });
      } catch (err) {
        console.error('Barcode rendering failed:', err);
      }
    }
  }, [value, format, height, width, displayValue]);

  if (format === 'QR Code') {
    return (
      <div className="flex flex-col items-center justify-center p-2 bg-white rounded border border-gray-100 shadow-sm">
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(value)}`}
          alt="QR Barcode"
          className="w-24 h-24"
        />
        {displayValue && <span className="text-[10px] font-mono mt-1 text-gray-900">{value}</span>}
      </div>
    );
  }

  return <svg ref={elementRef} className="mx-auto max-w-full" style={{ background: 'white' }}></svg>;
}

// Smart Barcode Console Component
function BarcodeSection({ 
  value, 
  onChange, 
  productName, 
  category, 
  variantName = null, 
  price = null,
  isEditing = false
}) {
  const [symbology, setSymbology] = useState("CODE128");
  const [styleFormat, setStyleFormat] = useState("GIO_PRD");
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!value && productName) {
      handleGenerate();
    }
  }, [productName, category, variantName, styleFormat, symbology]);

  const handleGenerate = () => {
    const newVal = generateBarcodeValue(productName, category, variantName, styleFormat, symbology);
    onChange(newVal);
  };

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-slate-800/40 p-4 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm space-y-3">
      <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 pb-2 mb-2">
        <span className="text-xs font-black uppercase text-gray-400 tracking-wider">Smart Barcode Console</span>
        {value && (
          <span className="text-[10px] bg-green-550/20 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-bold border border-green-100 dark:border-green-800">
            Active
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Symbology</label>
          <select
            className="w-full px-2 py-1 rounded bg-white dark:bg-dark-bg text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={symbology}
            onChange={(e) => {
              const sym = e.target.value;
              setSymbology(sym);
              if (value && (value.startsWith('GIO') || value.startsWith('CAT') || value.startsWith('VAR') || /^\d+$/.test(value))) {
                const newVal = generateBarcodeValue(productName, category, variantName, styleFormat, sym);
                onChange(newVal);
              }
            }}
          >
            <option value="CODE128">CODE128</option>
            <option value="CODE39">CODE39</option>
            <option value="EAN13">EAN-13 (Numeric)</option>
            <option value="UPC">UPC (Numeric)</option>
            <option value="QR Code">QR Code</option>
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Prefix Style</label>
          <select
            disabled={['EAN13', 'UPC'].includes(symbology)}
            className="w-full px-2 py-1 rounded bg-white dark:bg-dark-bg text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
            value={styleFormat}
            onChange={(e) => {
              const format = e.target.value;
              setStyleFormat(format);
              const newVal = generateBarcodeValue(productName, category, variantName, format, symbology);
              onChange(newVal);
            }}
          >
            <option value="GIO_PRD">GIO-PRD-XXXXX</option>
            <option value="GIO_SEQ">GIOXXXXXX</option>
            <option value="CAT_BASED">CAT-[NAME]-XXXXX</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <input
            type="text"
            className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Scan / Enter Barcode"
          />
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap"
          title="Auto Generate"
        >
          Generate
        </button>
      </div>

      {value ? (
        <div className="bg-white p-3 rounded-lg border border-gray-100 flex flex-col items-center space-y-2">
          <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Live Preview</div>
          <BarcodeRenderer value={value} format={symbology} height={40} width={1.5} displayValue={true} />
          
          <div className="flex flex-wrap justify-center gap-1.5 pt-1.5 border-t border-gray-100 w-full">
            <button
              type="button"
              onClick={handleCopy}
              className={`px-2 py-1 text-[10px] font-bold rounded flex items-center gap-1 transition-all ${copySuccess ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {copySuccess ? 'Copied ✓' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={() => downloadBarcodeAsPNG(value, productName, variantName, price, symbology)}
              className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[10px] font-bold rounded flex items-center gap-1 transition-colors"
            >
              PNG
            </button>
            <button
              type="button"
              onClick={() => downloadBarcodeAsPDF(value, productName, variantName, price, symbology)}
              className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-600 text-[10px] font-bold rounded flex items-center gap-1 transition-colors"
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => printBarcodeLabel(value, productName, variantName, price, symbology)}
              className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-600 text-[10px] font-bold rounded flex items-center gap-1 transition-colors"
            >
              Print
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-4 bg-white rounded-lg border border-dashed border-gray-200 text-xs text-gray-400">
          Enter value or click Generate to create barcode
        </div>
      )}
    </div>
  );
}


// Helper function for consistent product status calculation
function getProductStatus(stockQuantity, minStockLevel) {
  const qty = Number(stockQuantity) || 0;
  const minQty = Number(minStockLevel) || 10;
  if (qty <= 0) return "Out of Stock";
  if (qty <= minQty) return "Low Stock";
  return "In Stock";
}


export default function InventoryPage() {
  const { userRole } = useAuth();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { addNotification } = useContext(NotificationContext);
  const exportSuccess = useExportSuccess();
  const canManageInventory = userRole === 'OWNER' || userRole === 'SUPERADMIN';
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, title: "", message: "", type: "info" });
  const showAlert = (message, title = "Notification", type = "info") => {
    setCustomAlert({ show: true, title, message, type });
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [expandedProducts, setExpandedProducts] = useState(new Set());

  // Effect to handle query parameters for deep linking (e.g., ?status=Low%20Stock)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    if (statusParam) {
      setStatusFilter(statusParam);
    }

    // Handle initialBarcode from POS page
    if (location.state?.initialBarcode && location.state?.showCreate) {
      setNewProduct(prev => ({ ...prev, barcode: location.state.initialBarcode }));
      setShowAddModal(true);
      // Clear state to avoid reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.search, location.state]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateFilterActive, setDateFilterActive] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);
  const [selectedStockVariant, setSelectedStockVariant] = useState(null);
  const [stockQtyToAdd, setStockQtyToAdd] = useState("");
  const [stockReason, setStockReason] = useState("Restock");
  const [alertItems, setAlertItems] = useState([]);
  const [addImageError, setAddImageError] = useState("");
  const [editImageError, setEditImageError] = useState("");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [availableTaxRates, setAvailableTaxRates] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "",
    category: "",
    sku: "",
    hsn_code: "",
    unit: "Piece",
    gst: 0,
    barcode: "",
    stock: 0,
    opening_stock: 0,
    minimum_stock: 10,
    purchasePrice: "",
    sellingPrice: "",
    status: "Out of Stock",
    dateAdded: new Date().toISOString().split('T')[0],
    isActive: true,
    is_popular: false,
    is_recommended: false,
    is_special: false,
    variants: []
  });

  // Temporary state for category hierarchy in modals
  const [selectedMainCat, setSelectedMainCat] = useState("");
  const [selectedEditMainCat, setSelectedEditMainCat] = useState("");

  // Helper for safe date formatting
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return "Error";
    }
  };
  // Fetch products from backend on mount
  useEffect(() => {
    fetchCategoriesAndProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCategoriesAndProducts() {
    try {
      // Fetch categories, suppliers, tax rates, and units in parallel to maximize speed
      const [categoriesResponse, suppliersResponse, taxesResponse, unitsResponse] = await Promise.all([
        categoryAPI.getAllCategories().catch(err => { console.error("Failed to fetch categories", err); return { data: [] }; }),
        supplierAPI.getAllSuppliers().catch(err => { console.error("Failed to fetch suppliers", err); return { data: [] }; }),
        productAPI.getTaxRates().catch(err => { console.error("Failed to fetch tax rates", err); return { data: [] }; }),
        productAPI.getUnits().catch(err => { console.error("Failed to fetch units", err); return { data: [] }; })
      ]);

      // 1. Process categories
      let categoriesData = Array.isArray(categoriesResponse.data)
        ? categoriesResponse.data
        : categoriesResponse.data?.results || categoriesResponse.data?.data || [];
      setAvailableCategories(categoriesData);

      // 2. Process suppliers
      let suppliersData = Array.isArray(suppliersResponse.data)
        ? suppliersResponse.data
        : suppliersResponse.data?.results || suppliersResponse.data?.data || [];
      setAvailableSuppliers(suppliersData);

      // 3. Process tax rates
      let taxesData = Array.isArray(taxesResponse.data)
        ? taxesResponse.data
        : taxesResponse.data?.results || taxesResponse.data?.data || [];
      setAvailableTaxRates(taxesData);

      // 4. Process units
      let unitsData = Array.isArray(unitsResponse.data)
        ? unitsResponse.data
        : unitsResponse.data?.results || unitsResponse.data?.data || [];
      setAvailableUnits(unitsData);

    } catch (error) {
      console.error("Failed to fetch metadata in parallel", error);
    }

    // Fetch products after categories
    fetchProducts();
  }

  // Helper functions for variants
  const addVariantToState = (isEditing) => {
    const parentName = isEditing ? editing.name : newProduct.name;
    const parentCategory = isEditing ? 
      (categories.find(c => String(c.id) === String(editing.category_id))?.name || "") : 
      (categories.find(c => String(c.id) === String(newProduct.category_id))?.name || "");
    const vCount = isEditing ? (editing.variants || []).length : (newProduct.variants || []).length;
    const variantBarcode = generateBarcodeValue(parentName, parentCategory, `V${vCount + 1}`, 'GIO_PRD', 'CODE128');

    const newVariant = {
      tempId: Date.now() + Math.random(),
      name: "",
      barcode: variantBarcode,
      purchase_price: "",
      unit_price: "",
      stock: 0,
      minimum_stock: 5,
      is_active: true
    };
    if (isEditing) {
      setEditing(prev => ({ ...prev, variants: [...(prev.variants || []), newVariant] }));
    } else {
      setNewProduct(prev => ({ ...prev, variants: [...(prev.variants || []), newVariant] }));
    }
  };


  const removeVariantFromState = (idx, isEditing) => {
    if (isEditing) {
      const updated = [...(editing.variants || [])];
      updated.splice(idx, 1);
      setEditing({ ...editing, variants: updated });
    } else {
      const updated = [...(newProduct.variants || [])];
      updated.splice(idx, 1);
      setNewProduct({ ...newProduct, variants: updated });
    }
  };

  const updateVariantInState = (idx, field, value, isEditing) => {
    if (isEditing) {
      setEditing(prev => {
        const updated = [...(prev.variants || [])];
        if (!updated[idx]) return prev;
        updated[idx] = { ...updated[idx], [field]: value };
        return { ...prev, variants: updated };
      });
    } else {
      setNewProduct(prev => {
        const updated = [...(prev.variants || [])];
        if (!updated[idx]) return prev;
        updated[idx] = { ...updated[idx], [field]: value };
        return { ...prev, variants: updated };
      });
    }
  };

  // Helper function to generate SKU based on category and product name
  function generateSKU(productName, category, existingProducts) {
    // Create category code (first 3 letters of category, uppercase)
    const categoryCode = category.substring(0, 3).toUpperCase();

    // Create product code (first letters of each word, uppercase)
    const productCode = productName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 3);

    // Find the next sequential number for this category-product combination
    const baseCode = `${categoryCode}-${productCode}`;
    const existingCount = existingProducts.filter(p =>
      p.sku && p.sku.startsWith(baseCode)
    ).length;

    // Format: CAT-PRD-001, CAT-PRD-002, etc.
    return `${baseCode}-${String(existingCount + 1).padStart(3, '0')}`;
  }

  // Helper function to normalize products with dateAdded and isActive
  function normalizeProducts(products) {
    return products.map(p => {

      return {
        // Preserve original backend fields
        id: p.id,
        product_code: p.product_code,
        product_type: p.product_type || "fixed",
        name: p.name,
        image: p.image,
        is_active: p.is_active,
        created_at: p.created_at,
        updated_at: p.updated_at,
        purchase_price: p.purchase_price,
        unit_price: p.unit_price,
        tax_rate: p.tax_rate,
        minimum_stock: p.minimum_stock,
        opening_stock: p.opening_stock,
        profit: p.profit,
        reorder_quantity: p.reorder_quantity,
        preferred_supplier: p.preferred_supplier,
        preferred_supplier_id: p.preferred_supplier_id || (typeof p.preferred_supplier === 'object' ? p.preferred_supplier?.id : p.preferred_supplier) || null,
        supplier_name: p.preferred_supplier_name || (typeof p.preferred_supplier === 'object' ? p.preferred_supplier?.name : null) || (p.preferred_supplier_id ? `Supplier #${p.preferred_supplier_id}` : "No Supplier"),
        stock: Number(p.stock) || 0,
        // Map to frontend field names for display
        sku: p.product_code || p.sku || "",
        hsn_code: p.hsn_code || "",
        unit: p.unit || "Piece",
        sellingPrice: parseFloat(p.unit_price || p.selling_price || p.sellingPrice) || 0,
        purchasePrice: parseFloat(p.purchase_price || p.purchasePrice) || 0,
        tax: parseFloat(p.tax_rate || p.tax) || 0,
        barcode: p.barcode || "",
        asn_code: p.asn_code || "",
        dateAdded: p.dateAdded || p.created_at || new Date().toISOString().split('T')[0],
        category: p.category?.parent_name
          ? `${p.category.parent_name} > ${p.category.name}`
          : (p.category?.name || p.category || "Uncategorized"),
        categoryColor: typeof p.category === 'object' ? p.category?.color : null,
        category_id: p.category?.id || p.category_id || null,
        categoryId: p.category?.id || null,
        parent_category_id: p.category?.parent || p.parent_category_id || (typeof p.category === 'object' ? p.category?.parent : null) || null,
        status: getProductStatus(p.stock, p.minimum_stock),
        isActive: p.is_active !== undefined ? p.is_active : true,
        is_popular: p.is_popular || false,
        is_recommended: p.is_recommended || false,
        is_special: p.is_special || false,
        has_variants: p.has_variants || (Array.isArray(p.variants) && p.variants.length > 0),
        variants: (p.variants || []).map(v => ({
          ...v,
          tempId: v.id || `temp-${Math.random().toString(36).substr(2, 9)}`
        }))
      };
    });
  }

  async function fetchProducts() {
    setLoading(true);
    try {

      // Fetch products and inventory batches in parallel to reduce load time
      const [productsResponse, batchesResponse] = await Promise.all([
        productAPI.getAllProducts().catch(err => { console.error("Failed to fetch products", err); return { data: [] }; }),
        inventoryAPI.getBatches().catch(err => { console.error("Failed to fetch batches", err); return { data: [] }; })
      ]);

      let productsData = Array.isArray(productsResponse.data)
        ? productsResponse.data
        : productsResponse.data?.results || productsResponse.data?.data || [];

      let batchData = Array.isArray(batchesResponse.data)
        ? batchesResponse.data
        : batchesResponse.data?.results || [];

      if (productsData && Array.isArray(productsData) && productsData.length > 0) {
        // Normalize and enrich products with batch/inventory data if available
        const normalizedProducts = normalizeProducts(productsData);
        const enrichedProducts = normalizedProducts.map(product => {
          const productBatches = batchData.filter(b => b.product_id === product.id);
          const hasBatches = productBatches.length > 0;

          const totalStockFromBatches = productBatches.reduce((sum, b) => sum + (Number(b.remaining_quantity) || 0), 0);

          // Always use product.stock from the backend as the source of truth.
          // The product.stock field is authoritative — it is updated by all operations
          // (sales, purchases, manual adjustments). Batch remaining_quantity is only
          // used to enrich purchase price data, never to override product.stock.
          const finalStock = Number(product.stock) || 0;

          // Fallback to recent batch unit_cost if base purchasePrice is 0
          let finalPurchasePrice = product.purchasePrice;
          if (finalPurchasePrice === 0 && hasBatches) {
            const recentBatch = [...productBatches].sort((a, b) => new Date(b.created_at || b.received_at) - new Date(a.created_at || a.received_at))[0];
            if (recentBatch && recentBatch.unit_cost) {
              finalPurchasePrice = Number(recentBatch.unit_cost);
            }
          }

          return {
            ...product,
            batches: productBatches,
            totalStock: totalStockFromBatches,
            stock: finalStock,
            purchasePrice: finalPurchasePrice,
            status: getProductStatus(finalStock, product.minimum_stock)
          };
        });

        setProducts(enrichedProducts);
      } else {

        setProducts([]);
      }

    } catch (error) {

      setProducts([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = products.filter((p) => {
    const matchesSearch = (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = categoryFilter === "All Categories" || String(p.category_id) === String(categoryFilter);

    // Stock Status Filter
    const matchesStatus = statusFilter === "All Status" || p.status === statusFilter;

    // Only apply date filtering if it's active
    let matchesDate = true;
    if (dateFilterActive && p.dateAdded) {
      const productDate = new Date(p.dateAdded);
      // Ensure date is valid before comparing
      if (!isNaN(productDate.getTime())) {
        matchesDate =
          productDate.getDate() === selectedDate.getDate() &&
          productDate.getMonth() === selectedDate.getMonth() &&
          productDate.getFullYear() === selectedDate.getFullYear();
      }
    }

    return matchesSearch && matchesCategory && matchesStatus && matchesDate;
  });

  // Reset to first page when filtering changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter, dateFilterActive, selectedDate]);

  // Helper function to get available categories
  function getAvailableCategories() {
    // Return state-managed categories
    return availableCategories;
  }

  const categories = getAvailableCategories();
  const mainCategories = categories.filter(c => !c.parent);
  const statuses = ["All Status", "In Stock", "Low Stock", "Out of Stock"];

  async function saveProduct(updated) {
    // Frontend validation for image size
    if (updated.image instanceof File && updated.image.size > 1024 * 1024) {
      addNotification("error", "Upload Failed", "Image size must be less than 1MB");
      return;
    }

    try {
      if (!updated.name || !updated.category) {
        showAlert("Product Name and Category are mandatory.", "Missing Fields", "error");
        return;
      }

      // Ensure we have an ID
      if (!updated.id) {

        showAlert("Error: Product ID is missing", "System Error", "error");
        return;
      }

      let imageUrl = "";
      if (updated.image) {
        try {
          imageUrl = await uploadImage(updated.image, updated.id);
        } catch (uploadErr) {
          console.error("Image processing error:", uploadErr);
          addNotification("error", "Upload Error", "Failed to process image: " + uploadErr.message);
          return;
        }
      }

      // Map frontend fields to backend field names
      const formData = new FormData();
      formData.append("name", updated.name);
      formData.append("product_type", updated.product_type || "fixed");

      // Ensure product_code is not "undefined" string
      const sku = updated.product_code || updated.sku;
      if (sku) {
        formData.append("product_code", sku);
      }

      // Handle optional fields carefully for FormData
      if (updated.hsn_code) formData.append("hsn_code", updated.hsn_code);

      // Ensure unit has a valid value, default to "Piece" if missing
      formData.append("unit", updated.unit || "Piece");
      formData.append("purchase_price", parseFloat(updated.purchasePrice || updated.purchase_price) || 0);
      formData.append("unit_price", parseFloat(updated.sellingPrice || updated.unit_price) || 0);
      formData.append("tax_rate", parseFloat(updated.gst || updated.tax || updated.tax_rate) || 0);

      const isActive = updated.isActive !== undefined ? updated.isActive : (updated.is_active !== undefined ? updated.is_active : true);
      formData.append("is_active", isActive);

      formData.append("minimum_stock", parseInt(updated.minimum_stock) || 10);
      formData.append("reorder_quantity", parseInt(updated.reorder_quantity) || 10);

      // Only append supplier ID if it's a valid truthy value
      if (updated.preferred_supplier_id) {
        formData.append("preferred_supplier_id", updated.preferred_supplier_id);
      }

      // Append image URL (Firebase string)
      if (imageUrl) {
        formData.append("image", imageUrl);
      }

      // Add category_id if we have it
      if (updated.category_id) {
        formData.append("category_id", updated.category_id);
      } else if (updated.category && typeof updated.category === 'string') {
        // Find category ID from category name
        const selectedCategory = availableCategories.find(cat => cat.name === updated.category);
        if (selectedCategory) {
          formData.append("category_id", selectedCategory.id);
        }
      }

      if (updated.barcode) {
        formData.append("barcode", updated.barcode || "");
      }

      formData.append("is_popular", updated.is_popular || false);
      formData.append("is_recommended", updated.is_recommended || false);
      formData.append("is_special", updated.is_special || false);

      // Handle variants
      if (updated.variants && updated.variants.length > 0) {
        formData.append("variants", JSON.stringify(updated.variants));
      } else {
        formData.append("variants", "[]");
      }

      // Update on backend


      // Debug: Log FormData contents
      // Note: formData.entries() returns an iterator, need to loop or spread to see content
      const debugData = {};
      for (const [key, value] of formData.entries()) {
        debugData[key] = value;
      }
      console.log("Submitting Product Update:", updated.id, debugData);

      const response = await productAPI.updateProduct(updated.id, formData);

      const data = response.data;
      if (data) {
        // Normalize the response before updating state
        const normalizedData = normalizeProducts([data])[0];

        const updatedProducts = products.map((it) => {
          if (it.id === updated.id) {
            // Recalculate state exactly as fetchProducts does to prevent glitches
            const preservedBatches = it.batches || [];
            const hasBatches = preservedBatches.length > 0;
            const totalStockFromBatches = preservedBatches.reduce((sum, b) => sum + (Number(b.remaining_quantity) || 0), 0);

            const finalStock = hasBatches ? totalStockFromBatches : (Number(normalizedData.stock) || 0);

            let finalPurchasePrice = normalizedData.purchase_price !== undefined ? Number(normalizedData.purchase_price) : normalizedData.purchasePrice;
            if ((!finalPurchasePrice || finalPurchasePrice === 0) && hasBatches && preservedBatches[0].unit_cost) {
              finalPurchasePrice = Number(preservedBatches[0].unit_cost);
            }

            // Trust the server's normalized data for stock and variants
            const finalVariants = normalizedData.variants || [];
            const has_variants = normalizedData.has_variants || (finalVariants.length > 0);

            // Calculate aggregate stock if variants exist
            const variantStockSum = has_variants ? finalVariants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0) : null;

            return {
              ...it,       // Keep existing local-only fields (like batches)
              ...normalizedData, // Overwrite with server truth
              variants: finalVariants,
              has_variants: has_variants,
              stock: has_variants ? variantStockSum : (hasBatches ? totalStockFromBatches : (Number(normalizedData.stock) || 0)),
              purchasePrice: finalPurchasePrice,
              status: getProductStatus(has_variants ? (variantStockSum || 0) : (hasBatches ? totalStockFromBatches : (Number(normalizedData.stock) || 0)), normalizedData.minimum_stock)
            };
          }
          return it;
        });

        setProducts(updatedProducts);
        addNotification("success", "Product Updated", `${updated.name} has been updated successfully`);
      }
      setEditing(null);
    } catch (error) {
      console.error("Update Product Failed:", error.response?.data || error);
      const errorData = error.response?.data || {};

      let msg = "Failed to update product on server";
      if (errorData.detail) msg = errorData.detail;
      else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
        // Format field errors
        msg = Object.entries(errorData)
          .map(([key, val]) => `${key}: ${Array.isArray(val) ? val[0] : val}`)
          .join(", ");
      }

      addNotification("error", "Update Failed", msg);
    }
  }

  async function handleAddProduct() {

    try {
      // Check if categories exist
      if (availableCategories.length === 0) {

        showAlert("⚠️ Please create product categories first!\n\nGo to Admin → Categories to add categories.", "No Categories Available", "error");
        return;
      }

      // Validate required fields
      if (!newProduct.name || !newProduct.category) {
        showAlert("Please fill in Product Name and Category", "Missing Fields", "error");
        return;
      }

      // Validate numeric fields
      const sellingPrice = parseFloat(newProduct.sellingPrice);
      const stock = parseFloat(newProduct.stock);
      const purchasePrice = newProduct.purchasePrice ? parseFloat(newProduct.purchasePrice) : 0;
      const hasVariants = newProduct.variants && newProduct.variants.length > 0;

      if (!hasVariants && (isNaN(sellingPrice) || sellingPrice <= 0)) {
        showAlert("❌ Selling Price is required and must be greater than 0", "Invalid Price", "error");
        return;
      }

      if (isNaN(stock) || stock < 0) {

        showAlert("❌ Stock must be a valid number (0 or more)", "Invalid Stock", "error");
        return;
      }

      // Find category ID from category name
      const selectedCategory = availableCategories.find(cat => cat.name === newProduct.category);
      if (!selectedCategory) {

        showAlert("Please select a valid category", "Invalid Category", "error");
        return;
      }

      // Generate product code (SKU) based on category and product name
      const productCode = newProduct.sku || generateSKU(newProduct.name, newProduct.category, products);

      let imageUrl = "";
      if (newProduct.image) {
        try {
          imageUrl = await uploadImage(newProduct.image, "new");
        } catch (uploadErr) {
          console.error("Image processing error:", uploadErr);
          addNotification("error", "Upload Error", "Failed to process image: " + uploadErr.message);
          return;
        }
      }

      // Create FormData
      const formData = new FormData();
      formData.append("name", newProduct.name);
      formData.append("product_type", newProduct.product_type || "fixed");
      formData.append("product_code", productCode);
      formData.append("hsn_code", newProduct.hsn_code || "");
      formData.append("unit", newProduct.unit);
      formData.append("category_id", selectedCategory.id);
      formData.append("opening_stock", parseFloat(newProduct.opening_stock) || 0);
      formData.append("purchase_price", purchasePrice || 0);
      formData.append("unit_price", sellingPrice || 0);
      formData.append("tax_rate", newProduct.gst || 0);
      formData.append("is_active", newProduct.isActive !== undefined ? newProduct.isActive : true);
      formData.append("minimum_stock", parseInt(newProduct.minimum_stock) || 10);
      formData.append("reorder_quantity", parseInt(newProduct.reorder_quantity) || 10);
      if (newProduct.preferred_supplier_id) {
        formData.append("preferred_supplier_id", newProduct.preferred_supplier_id);
      }

      formData.append("is_popular", newProduct.is_popular || false);
      formData.append("is_recommended", newProduct.is_recommended || false);
      formData.append("is_special", newProduct.is_special || false);

      if (newProduct.barcode) {
        formData.append("barcode", newProduct.barcode || "");
      }

      // Handle variants
      if (newProduct.variants && newProduct.variants.length > 0) {
        formData.append("variants", JSON.stringify(newProduct.variants));
      } else {
        formData.append("variants", "[]");
      }

      if (imageUrl) {
        formData.append("image", imageUrl);
      }

      const response = await productAPI.createProduct(formData);

      // Backend returns the created product - handle different response structures
      const savedProduct = response.data?.data || response.data;

      // Normalize the created product before adding to state
      const normalizedProduct = normalizeProducts([savedProduct])[0];
      const updatedProducts = [...products, normalizedProduct];
      setProducts(updatedProducts);
      addNotification("product", "Product Added", `${newProduct.name} added successfully to backend`);

      // Reset form and close modal
      setNewProduct({
        name: "",
        category: "",
        sku: "",
        hsn_code: "",
        unit: "Piece",
        gst: 0,
        barcode: "",
        stock: 0,
        purchasePrice: "",
        sellingPrice: "",
        status: "Out of Stock",
        dateAdded: new Date().toISOString().split('T')[0],
        isActive: true,
        image: null,
        variants: []
      });
      setShowAddModal(false);

      try { exportSuccess.showExportSuccess(`Product ${newProduct.name} added successfully!`); } catch (e) { }
    } catch (error) {
      console.error("Add Product Failed:", error);
      const errorData = error.response?.data || {};

      let msg = "Failed to add product";
      if (errorData.detail) msg = errorData.detail;
      else if (errorData.message) msg = errorData.message;
      else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
        // Join field-specific errors
        msg = Object.entries(errorData)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(", ");
      } else if (error.message) msg = error.message;

      if (typeof addNotification === 'function') {
        addNotification("error", "Add Failed", msg);
      }
    }

  }

  async function deleteProduct(id) {
    const product = products.find(p => p.id === id);
    if (!window.confirm(`Delete ${product?.name || 'this product'}? This cannot be undone.`)) {
      return;
    }

    try {
      // Delete from backend
      await productAPI.deleteProduct(id);
      const updatedProducts = products.filter((it) => it.id !== id);
      setProducts(updatedProducts);
      addNotification("product", "Product Deleted", `${product?.name} has been removed`);
    } catch (error) {

      addNotification("error", "Delete Failed", "Failed to delete product from server");
    }

  }

  function handleOpenAddStockModal(product, variant = null) {
    setSelectedStockProduct(product);
    setSelectedStockVariant(variant);
    setStockQtyToAdd("");
    setStockReason("Restock");
    setShowAddStockModal(true);
  }

  async function handleAdjustStockSubmit() {
    if (!selectedStockProduct) return;
    const qty = parseFloat(stockQtyToAdd);
    if (isNaN(qty) || qty <= 0) {
      showAlert("Please enter a valid positive quantity.", "Invalid Quantity", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        product_id: selectedStockProduct.id,
        adjustment_quantity: qty,
        reason: stockReason || "Restock"
      };
      if (selectedStockVariant) {
        payload.variant_id = selectedStockVariant.id;
      }
      await inventoryAPI.adjustStock(payload);
      const targetName = selectedStockVariant 
        ? `${selectedStockProduct.name} (${selectedStockVariant.name})` 
        : selectedStockProduct.name;
      addNotification("product", "Stock Updated", `Added ${qty} units of stock to ${targetName}`);
      setShowAddStockModal(false);
      setSelectedStockProduct(null);
      setSelectedStockVariant(null);
      setStockQtyToAdd("");
      setStockReason("Restock");
      fetchProducts();
    } catch (error) {
      console.error("Stock adjustment failed:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update stock";
      addNotification("error", "Adjustment Failed", errorMsg);
    } finally {
      setLoading(false);
    }
  }

  // Export Products to Excel
  function handleExportProducts() {
    try {
      const worksheet = XLSX.utils.json_to_sheet(products);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");
      XLSX.writeFile(workbook, `inventory_${new Date().toISOString().split('T')[0]}.xlsx`);
      addNotification("product", "Export Successful", `${products.length} products exported from Inventory`);
      try { exportSuccess.showExportSuccess(`${products.length} products exported from Inventory`); } catch (e) { }
    } catch (error) {
      alert("⚠️ Error exporting file: " + error.message);
    }
  }

  // Import Products from Excel
  function handleImportProducts(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const newProducts = jsonData.map((item, idx) => {
          const product = {
            id: `p_${Date.now()}_${idx}`,
            name: item.name || "Unnamed",
            category: item.category || "General",
            sku: item.sku || generateSKU(item.name || "Unnamed", item.category || "General", [...products].slice(0, idx)),
            barcode: item.barcode || `BAR-${idx}`,
            stock: parseInt(item.stock) || 0,
            purchasePrice: parseFloat(item.purchasePrice) || 0,
            sellingPrice: parseFloat(item.sellingPrice) || 0,
            tax: parseFloat(item.tax) || 0,
            status: getProductStatus(item.stock, 10)
          };
          return product;
        });

        setProducts([...products, ...newProducts]);
        setTimeout(() => {
          addNotification("product", "Import Successful", `${newProducts.length} products imported into Inventory`);
          try { exportSuccess.showExportSuccess(`${newProducts.length} products imported into Inventory`); } catch (e) { }
        }, 200);
      } catch (error) {
        alert("⚠️ Error importing file: " + error.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  }

  // Calculate statistics using the pre-calculated status for consistency
  const totalProducts = products.length;
  const inStockProducts = products.filter(p => p.status === "In Stock").length;
  const lowStockProducts = products.filter(p => p.status === "Low Stock").length;
  const outOfStockProducts = products.filter(p => p.status === "Out of Stock").length;
  const totalInventoryValue = products.reduce((sum, p) => sum + ((p.stock || 0) * (p.purchasePrice || 0)), 0);

  // Calendar helper functions

  // Check if user has permission to view inventory
  if (!hasPermission(userRole, 'view_inventory')) {
    return (
      <div className="min-h-screen bg-white dark:bg-dark-bg p-4 md:p-8 flex items-center justify-center">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-900 dark:text-red-200 mb-2">Access Denied</h2>
          <p className="text-red-700 dark:text-red-300">You don't have permission to view inventory. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg p-4 md:p-8 transition-colors">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Inventory Management</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">Spice Garden Restaurant - Track stock levels and product information</p>
          </div>
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={async () => {
                try {
                  const res = await productAPI.checkStockAlerts();
                  const { alerts_generated, supplier_notifications, alert_items } = res.data;
                  addNotification("info", "Stock Check Complete", `Alerts: ${alerts_generated}, Notifications: ${supplier_notifications}`);
                  if (alert_items && alert_items.length > 0) {
                    setAlertItems(alert_items);
                    setShowAlertsModal(true);
                  } else {
                    alert("✅ All items are in stock! No new alerts generated.");
                  }
                  fetchProducts(); // Refresh to see updates
                } catch (e) {
                  addNotification("error", "Check Failed", "Could not check stock alerts");
                }
              }}
              className="px-6 py-3 flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-bold transition-colors"
            >
              <AlertTriangle className="w-5 h-5" />
              Check Alerts
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExportProducts}
              className="px-6 py-3 flex items-center gap-2 bg-white dark:bg-dark-card border-2 border-blue-200 dark:border-dark-border text-gray-900 dark:text-white rounded-lg font-bold hover:border-blue-500 transition-colors"
            >
              <Download className="w-5 h-5" />
              Export
            </motion.button>

            {canManageInventory && (
              <>
                <label className="px-6 py-3 flex items-center gap-2 bg-white dark:bg-dark-card border-2 border-blue-200 text-gray-900 dark:text-white rounded-lg font-bold hover:border-blue-500 transition-colors cursor-pointer">
                  <Upload className="w-5 h-5" />
                  Import Excel
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportProducts}
                    className="hidden"
                  />
                </label>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    if (availableCategories.length === 0) {
                      alert("⚠️ Please create product categories first!\n\nGo to Admin → Categories to add categories.");
                      return;
                    }
                    setShowAddModal(true);
                  }}
                  className="px-6 py-3 flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Product
                </motion.button>
              </>
            )}
          </div>
        </div>
      </motion.div >

      {/* Stats Cards */}
      < motion.div
        initial={{ opacity: 0 }
        }
        animate={{ opacity: 1 }}
        transition={{ staggerChildren: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        {
          [
            { title: "Total Products", value: totalProducts, subtitle: "Active inventory items", icon: Package, color: "bg-blue-50", textColor: "text-blue-600", filterValue: "All Status" },
            { title: "In Stock", value: inStockProducts, subtitle: "Products available", icon: Package, color: "bg-blue-50", textColor: "text-blue-600", filterValue: "In Stock" },
            { title: "Low Stock", value: lowStockProducts, subtitle: "Items need restocking", icon: AlertTriangle, color: "bg-yellow-50", textColor: "text-yellow-600", filterValue: "Low Stock" },
            { title: "Out of Stock", value: outOfStockProducts, subtitle: "Unavailable products", icon: AlertTriangle, color: "bg-red-50", textColor: "text-red-600", filterValue: "Out of Stock" },
          ].map((stat, idx) => {
            const Icon = stat.icon;
            const isActive = statusFilter === stat.filterValue;

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => setStatusFilter(isActive ? "All Status" : stat.filterValue)}
                className={`${stat.color} dark:bg-opacity-10 rounded-lg p-6 border transition-all cursor-pointer ${isActive ? 'ring-2 ring-offset-2 ring-blue-500 border-blue-500 shadow-md' : 'border-blue-200 dark:border-dark-border hover:shadow-lg'}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">{stat.title}</p>
                    <p className={`${stat.textColor} dark:brightness-125 text-3xl font-bold mb-1`}>{stat.value}</p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">{stat.subtitle}</p>
                  </div>
                  <div className={`${stat.textColor} opacity-20`}>
                    <Icon className="w-8 h-8" />
                  </div>
                </div>
              </motion.div>
            );
          })
        }
      </motion.div >

      {/* Inventory Value Card */}
      < motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 rounded-lg p-6 border border-indigo-200 dark:border-slate-700 mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-2">Total Inventory Value</p>
            <p className="text-purple-600 dark:text-purple-400 text-3xl font-bold">₹{totalInventoryValue.toLocaleString()}</p>
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">Current stock valuation</p>
          </div>
          <div className="text-purple-600 opacity-20">
            <IndianRupee className="w-10 h-10" />
          </div>
        </div>
      </motion.div >

      {/* Search and Filters */}
      < motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-dark-card rounded-lg border border-blue-200 dark:border-dark-border p-6 mb-6"
      >
        <div className="flex flex-col gap-4">
          {/* Search with Calendar Dropdowns */}
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-3.5 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, SKU, or barcode..."
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
              />
            </div>
            {/* Date Selector */}
            <div className="flex gap-2 items-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <select
                value={String(selectedDate.getDate())}
                onChange={(e) => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(parseInt(e.target.value));
                  setSelectedDate(newDate);
                  setDateFilterActive(true);
                }}
                className="px-2 py-2.5 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-16"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={String(day)}>{day}</option>
                ))}
              </select>
              <select
                value={String(selectedDate.getMonth())}
                onChange={(e) => {
                  const newDate = new Date(selectedDate);
                  newDate.setMonth(parseInt(e.target.value));
                  setSelectedDate(newDate);
                  setDateFilterActive(true);
                }}
                className="px-2 py-2.5 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-20"
              >
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
                  <option key={idx} value={String(idx)}>{month}</option>
                ))}
              </select>
              <select
                value={String(selectedDate.getFullYear())}
                onChange={(e) => {
                  const newDate = new Date(selectedDate);
                  newDate.setFullYear(parseInt(e.target.value));
                  setSelectedDate(newDate);
                  setDateFilterActive(true);
                }}
                className="px-2 py-2.5 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm font-medium w-20"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                  <option key={year} value={String(year)}>{year}</option>
                ))}
              </select>
              <button
                onClick={() => setDateFilterActive(false)}
                className="px-3 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-3 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-medium"
            >
              <option value="All Categories">All Categories</option>
              {mainCategories.map(main => {
                const subs = categories.filter(c => c.parent === main.id);
                if (subs.length === 0) {
                  return <option key={main.id} value={main.id}>{main.name}</option>;
                }
                return (
                  <optgroup key={main.id} label={main.name}>
                    {subs.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </optgroup>
                );
              })}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-medium"
            >
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Products Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-dark-card rounded-lg border border-blue-200 dark:border-dark-border overflow-hidden shadow-sm"
      >
        <div className="p-6 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Products ({filtered.length})</h2>
        </div>

        {
          filtered.length === 0 ? (
            <EmptyState
              preset="box"
              title="No products found"
              message="Try searching for a different product name or category."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-50 dark:bg-slate-700/50 border-b border-blue-200 dark:border-dark-border">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 dark:text-white">Product</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 dark:text-white">Category</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">Price (S/P)</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">Stock</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">Tags</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900 dark:text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                  {filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((p, idx) => (
                    <React.Fragment key={p.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`hover:bg-blue-50 dark:hover:bg-slate-700/30 transition-colors ${expandedProducts.has(p.id) ? 'bg-blue-50/50 dark:bg-slate-700/20' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {p.has_variants && (
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedProducts);
                                  if (newExpanded.has(p.id)) newExpanded.delete(p.id);
                                  else newExpanded.add(p.id);
                                  setExpandedProducts(newExpanded);
                                }}
                                className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                              >
                                {expandedProducts.has(p.id) ? (
                                  <ChevronDown className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            )}
                            {!p.has_variants && <div className="w-6" />}
                            <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-800/50 flex flex-shrink-0 items-center justify-center overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm group-hover:shadow-md transition-shadow">
                              {p.image ? (
                                <img
                                  src={resolveMediaUrl(p.image)}
                                  alt={p.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                              ) : (
                                <div className="flex flex-col items-center justify-center opacity-40">
                                  <Package className="w-5 h-5 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 dark:text-white">{p.name}</p>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-400 font-mono tracking-tighter uppercase mr-1">{p.hsn_code || "No HSN"}</span>
                                {p.asn_code && (
                                  <span className="text-[9px] text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 px-1.5 py-0.5 rounded-full font-bold border border-indigo-100 dark:border-indigo-900/50">
                                    {p.asn_code}
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">{p.unit}</span>
                                {p.variants && p.variants.length > 0 && (
                                  <span className="bg-blue-600 text-white dark:bg-blue-600 dark:text-white px-2 py-0.5 rounded-full text-[9px] font-black border border-blue-400 shadow-sm animate-pulse-slow">
                                    {p.variants.length} OPTIONS
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left">
                          <span
                            className="px-2 py-1 rounded-md text-xs font-bold border whitespace-nowrap"
                            style={{
                              backgroundColor: p.categoryColor ? `${p.categoryColor}15` : '#f3f4f6',
                              color: p.categoryColor || '#4b5563',
                              borderColor: p.categoryColor ? `${p.categoryColor}30` : '#e5e7eb'
                            }}
                          >
                            {p.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="font-bold text-green-600 dark:text-green-400 text-sm">₹{p.sellingPrice.toFixed(2)}</span>
                            <span className="text-[10px] text-gray-400 font-medium">₹{p.purchasePrice.toFixed(2)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`font-black text-base ${p.stock <= p.minimum_stock ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                              {p.has_variants
                                ? p.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
                                : p.stock
                              }
                              <span className="text-[10px] text-gray-400 font-normal uppercase ml-1">{p.unit}</span>
                            </span>
                            <span className={`text-[9px] font-bold uppercase tracking-tighter -mt-1 ${p.status === "In Stock" ? "text-green-500" : p.status === "Low Stock" ? "text-yellow-500" : "text-red-500"}`}>
                              {p.status}
                            </span>
                            {/* Conditional Add Stock Button */}
                            {((p.has_variants
                              ? p.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
                              : p.stock) <= (p.minimum_stock || 5) || p.status === "Low Stock" || p.status === "Out of Stock") && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleOpenAddStockModal(p)}
                                className="mt-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-[10px] font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-1 uppercase tracking-wider whitespace-nowrap"
                              >
                                <Plus className="w-3 h-3" />
                                Add Stock
                              </motion.button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-wrap justify-center gap-1">
                            {p.is_popular && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-black rounded border border-green-200 uppercase tracking-tighter">Popular</span>
                            )}
                            {p.is_recommended && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-black rounded border border-blue-200 uppercase tracking-tighter">Recommended</span>
                            )}
                            {p.is_special && (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[9px] font-black rounded border border-purple-200 uppercase tracking-tighter">Special</span>
                            )}
                            {!p.is_popular && !p.is_recommended && !p.is_special && (
                              <span className="text-[10px] text-gray-300 italic">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              if (!canManageInventory) return;
                              const updatedProduct = { ...p, isActive: !p.isActive };
                              saveProduct(updatedProduct);
                            }}
                            disabled={!canManageInventory}
                            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold transition-all ${p.isActive
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50"
                              : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                              } ${!canManageInventory ? 'cursor-not-allowed opacity-70' : ''}`}
                          >
                            {p.isActive ? "✓ Active" : "Inactive"}
                          </motion.button>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {canManageInventory && (
                            <div className="flex items-center justify-center gap-2 transition-opacity group">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setEditing(p)}
                                className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 text-blue-600 dark:text-blue-400 transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => deleteProduct(p.id)}
                                className="p-2 rounded-lg bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/50 text-red-600 dark:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </motion.button>
                            </div>
                          )}
                        </td>
                      </motion.tr>
                      {expandedProducts.has(p.id) && p.variants && p.variants.length > 0 && (
                        <tr className="bg-gray-50/50 dark:bg-slate-800/20 border-l-4 border-l-blue-500">
                          <td colSpan="6" className="px-12 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {p.variants.map((v, vIdx) => (
                                <div key={v.id || v.tempId || vIdx} className="bg-white dark:bg-dark-card p-3 rounded-lg border border-blue-100 dark:border-dark-border shadow-sm">
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="font-bold text-blue-600 dark:text-blue-400 text-sm">{v.name}</span>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v.stock <= (v.minimum_stock || 5) ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                      {v.stock <= (v.minimum_stock || 5) ? 'LOW STOCK' : 'IN STOCK'}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                                    <div className="text-gray-500">Barcode: <span className="text-gray-900 dark:text-gray-300 font-medium">{v.barcode || '-'}</span></div>
                                    <div className="text-gray-500 text-right">Stock: <span className="text-gray-900 dark:text-gray-300 font-bold">{v.stock}</span></div>
                                    <div className="text-gray-500">Purchase: <span className="text-gray-900 dark:text-gray-300 font-medium">₹{v.purchase_cost || v.purchase_price || '-'}</span></div>
                                    <div className="text-gray-500 text-right">Selling: <span className="text-blue-600 dark:text-blue-400 font-bold">₹{v.unit_price}</span></div>
                                    <div className="text-gray-500 col-span-2 mt-1 pt-1 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                                      <span>Calculated Profit:</span>
                                      <span className="text-green-600 font-black">₹{(parseFloat(v.unit_price || 0) - parseFloat(v.purchase_cost || v.purchase_price || 0)).toFixed(2)}</span>
                                    </div>
                                  </div>
                                  {v.stock <= (v.minimum_stock || 5) && (
                                    <button
                                      onClick={() => handleOpenAddStockModal(p, v)}
                                      className="mt-2.5 w-full py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-[10px] font-bold rounded shadow-sm transition-all duration-200 flex items-center justify-center gap-1 uppercase tracking-wider"
                                    >
                                      <Plus className="w-3 h-3" />
                                      Add Stock
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </motion.div >

      {/* Pagination Controls */}
      {filtered.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-4 border-t border-gray-200 dark:border-dark-border pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-bold">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-bold">{Math.min(currentPage * itemsPerPage, filtered.length)}</span> of <span className="font-bold">{filtered.length}</span> results
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-dark-card dark:text-gray-300 dark:border-dark-border dark:hover:bg-slate-800"
            >
              Previous
            </button>

            <div className="flex items-center gap-2">
              {[...Array(Math.ceil(filtered.length / itemsPerPage))].map((_, i) => {
                const pageNumber = i + 1;
                // Show current page, first, last, and surrounding pages
                if (
                  pageNumber === 1 ||
                  pageNumber === Math.ceil(filtered.length / itemsPerPage) ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg ${currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-dark-card dark:text-gray-300 dark:border-dark-border dark:hover:bg-slate-800"
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={i} className="text-gray-500">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filtered.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(filtered.length / itemsPerPage)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-dark-card dark:text-gray-300 dark:border-dark-border dark:hover:bg-slate-800"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-dark-card rounded-lg p-8 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Add New Product</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Product Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  placeholder="e.g. Tomato Soup"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">ASN Code</label>
                <input
                  type="text"
                  disabled
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-card dark:text-gray-400 cursor-not-allowed"
                  value="Auto-generated"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Main Category *</label>
                <select
                  required
                  className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedMainCat || categories.find(c => String(c.id) === String(newProduct.category_id))?.parent || ""}
                  onChange={(e) => {
                    const mainId = e.target.value;
                    setSelectedMainCat(mainId);
                    setNewProduct({ ...newProduct, category_id: "", category: "" });
                  }}
                >
                  <option value="">Select Main Category</option>
                  {mainCategories.map(main => (
                    <option key={main.id} value={main.id}>{main.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Subcategory *</label>
                <select
                  required
                  disabled={!selectedMainCat && !categories.find(c => String(c.id) === String(newProduct.category_id))?.parent}
                  className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  value={newProduct.category_id || ""}
                  onChange={(e) => {
                    const catId = e.target.value;
                    const cat = categories.find(c => String(c.id) === String(catId));
                    setNewProduct({ ...newProduct, category_id: catId, category: cat?.name || "" });
                  }}
                >
                  <option value="">Select Subcategory</option>
                  {categories
                    .filter(c => String(c.parent) === String(selectedMainCat || categories.find(curr => String(curr.id) === String(newProduct.category_id))?.parent || ""))
                    .map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Primary Supplier *</label>
                <select
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-orange-200 dark:border-orange-900/30 dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={newProduct.preferred_supplier_id || ""}
                  onChange={(e) => setNewProduct({ ...newProduct, preferred_supplier_id: e.target.value })}
                >
                  <option value="">Select Primary Supplier</option>
                  {availableSuppliers.map(sup => (
                    <option key={sup.id} value={sup.id}>{sup.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  HSN Code
                  <span className="ml-2 text-xs font-normal text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">Smart Search</span>
                </label>
                <SmartHSNInput
                  value={newProduct.hsn_code}
                  gstValue={newProduct.gst}
                  productName={newProduct.name}
                  mainCategory={categories.find(c => String(c.id) === String(selectedMainCat))?.name || ""}
                  subCategory={categories.find(c => String(c.id) === String(newProduct.category_id))?.name || ""}
                  onHSNChange={(hsn, gst) => setNewProduct(prev => ({ ...prev, hsn_code: hsn, gst: gst }))}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Barcode</label>
                <BarcodeSection
                  value={newProduct.barcode}
                  onChange={(val) => setNewProduct(prev => ({ ...prev, barcode: val }))}
                  productName={newProduct.name}
                  category={categories.find(c => String(c.id) === String(newProduct.category_id))?.name || ""}
                  price={newProduct.sellingPrice}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Product Type *</label>
                <select
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-purple-200 dark:border-purple-900/30 dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={newProduct.product_type || "fixed"}
                  onChange={(e) => {
                    const type = e.target.value;
                    setNewProduct(prev => {
                      const parentCategory = categories.find(c => String(c.id) === String(prev.category_id))?.name || "";
                      const mainBarcode = prev.barcode || generateBarcodeValue(prev.name, parentCategory, null, 'GIO_PRD', 'CODE128');
                      const updated = {
                        ...prev,
                        barcode: mainBarcode,
                        product_type: type,
                        unit: type === 'weight_based' ? 'Kg' : prev.unit,
                      };
                      if (type === 'variant' && (!prev.variants || prev.variants.length === 0)) {
                        const variantBarcode = generateBarcodeValue(prev.name, parentCategory, "V1", 'GIO_PRD', 'CODE128');
                        updated.variants = [{
                          tempId: Date.now(),
                          name: "",
                          barcode: variantBarcode,
                          purchase_price: "",
                          unit_price: "",
                          stock: 0,
                          minimum_stock: 5,
                          is_active: true
                        }];
                      } else if (type !== 'variant') {
                        updated.variants = [];
                      }
                      return updated;
                    });

                  }}
                >
                  <option value="fixed">Fixed Product</option>
                  <option value="variant">Variant Product</option>
                  <option value="weight_based">Weight-Based</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Unit</label>
                  <select
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newProduct.unit}
                    onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                  >
                    {availableUnits.map(u => (
                      <option key={u.id} value={u.symbol}>{u.name} ({u.symbol})</option>
                    ))}
                    {/* Fallback */}
                    {availableUnits.length === 0 && ["Piece", "Kg", "Litre", "Box", "Meter"].map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">GST %</label>
                  <select
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newProduct.gst}
                    onChange={(e) => setNewProduct({ ...newProduct, gst: Number(e.target.value) })}
                  >
                    <option value={0}>0%</option>
                    {availableTaxRates.map(tax => (
                      <option key={tax.id} value={Number(tax.gst_percentage)}>
                        {tax.name}
                      </option>
                    ))}
                    {/* Fallbacks if API fails */}
                    {availableTaxRates.length === 0 && [5, 12, 18, 28].map(rate => (
                      <option key={rate} value={rate}>{rate}%</option>
                    ))}
                  </select>
                </div>
              </div>


              {newProduct.product_type === 'variant' && (
                <div className="col-span-2 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-6 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Product Variants</h3>
                    <button
                      type="button"
                      onClick={() => addVariantToState(false)}
                      className="px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-sm font-bold hover:bg-blue-100 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Variant
                    </button>
                  </div>

                  <div className="space-y-3">
                    {newProduct.variants.map((v, idx) => (
                      <div key={v.tempId} className="p-4 rounded-xl border border-blue-100 dark:border-blue-900/30 bg-blue-50/30 dark:bg-blue-900/10 relative group">
                        <button
                          type="button"
                          onClick={() => removeVariantFromState(idx, false)}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-1">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Variant Name</label>
                            <input
                              type="text"
                              value={v.name}
                              onChange={(e) => updateVariantInState(idx, 'name', e.target.value, false)}
                              placeholder="e.g. Small / Red"
                              className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <label className="block text-[10px] font-bold text-gray-500 uppercase">Barcode</label>
                              {v.barcode && (
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(v.barcode);
                                      addNotification("success", "Copied", "Variant barcode copied!");
                                    }}
                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Copy
                                  </button>
                                  <span className="text-gray-300">|</span>
                                  <button
                                    type="button"
                                    onClick={() => downloadBarcodeAsPNG(v.barcode, newProduct.name, v.name || `Var #${idx+1}`, v.unit_price)}
                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    PNG
                                  </button>
                                  <span className="text-gray-300">|</span>
                                  <button
                                    type="button"
                                    onClick={() => printBarcodeLabel(v.barcode, newProduct.name, v.name || `Var #${idx+1}`, v.unit_price)}
                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Print
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1.5">
                              <input
                                type="text"
                                value={v.barcode}
                                onChange={(e) => updateVariantInState(idx, 'barcode', e.target.value, false)}
                                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                                placeholder="Auto or Enter"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const val = generateBarcodeValue(newProduct.name, categories.find(c => String(c.id) === String(newProduct.category_id))?.name, v.name || `V${idx+1}`, 'GIO_PRD', 'CODE128');
                                  updateVariantInState(idx, 'barcode', val, false);
                                }}
                                className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-bold whitespace-nowrap hover:bg-blue-200"
                              >
                                Gen
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Purchase</label>
                              <input
                                type="number"
                                value={v.purchase_price}
                                onChange={(e) => updateVariantInState(idx, 'purchase_price', e.target.value, false)}
                                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Selling</label>
                              <input
                                type="number"
                                value={v.unit_price}
                                onChange={(e) => updateVariantInState(idx, 'unit_price', e.target.value, false)}
                                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {newProduct.product_type !== 'variant' && (
                <>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Purchase Price *</label>
                    <input
                      type="number"
                      required
                      className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newProduct.purchasePrice}
                      onChange={(e) => setNewProduct({ ...newProduct, purchasePrice: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Selling Price *</label>
                    <input
                      type="number"
                      required
                      className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newProduct.sellingPrice}
                      onChange={(e) => setNewProduct({ ...newProduct, sellingPrice: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Product Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        setAddImageError("");
                        const file = e.target.files[0];
                        if (file && file.size > 1024 * 1024) {
                          addNotification("error", "File too large", "Please upload an image smaller than 1MB.");
                          setAddImageError("File too large. Please upload an image smaller than 1MB.");
                          e.target.value = null;
                          return;
                        }
                        setNewProduct({ ...newProduct, image: file });
                      }}
                      className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${addImageError ? 'border border-red-500 rounded-lg p-1' : ''}`}
                    />
                    {addImageError ? (
                      <p className="text-xs text-red-500 mt-2 font-medium">{addImageError}</p>
                    ) : (
                      <p className="text-[10px] text-gray-500 mt-1 uppercase font-bold tracking-wider italic">Max size: 1MB (For better performance)</p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddProduct}
                className="flex-1 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors"
              >
                Add Product
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowAddModal(false);
                  setAddImageError("");
                }}
                className="flex-1 py-3 rounded-lg border-2 border-gray-300 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Modal */}
      {
        editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setEditing(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-card rounded-lg p-8 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edit Product</h2>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Product Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">ASN Code</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-100 dark:border-dark-border dark:bg-dark-card dark:text-gray-400 cursor-not-allowed"
                    value={editing.asn_code || "Auto-generated"}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Category</label>
                  <select
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedEditMainCat || categories.find(c => String(c.id) === String(editing.category_id))?.parent || ""}
                    onChange={(e) => {
                      const mainId = e.target.value;
                      setSelectedEditMainCat(mainId);
                      setEditing({ ...editing, category_id: "", category: "" });
                    }}
                  >
                    <option value="">Select Main Category</option>
                    {mainCategories.map(main => (
                      <option key={main.id} value={main.id}>{main.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Subcategory *</label>
                  <select
                    required
                    disabled={!selectedEditMainCat && !categories.find(c => String(c.id) === String(editing.category_id))?.parent}
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    value={editing.category_id || ""}
                    onChange={(e) => {
                      const catId = e.target.value;
                      const cat = categories.find(c => String(c.id) === String(catId));
                      // Auto-generate new SKU when category changes
                      const newSKU = generateSKU(editing.name, cat?.name || "", products.filter(p => p.id !== editing.id));
                      setEditing({ ...editing, category_id: catId, category: cat?.name || "", sku: newSKU });
                    }}
                  >
                    <option value="">Select Subcategory</option>
                    {categories
                      .filter(c => String(c.parent) === String(selectedEditMainCat || categories.find(curr => String(curr.id) === String(editing.category_id))?.parent || ""))
                      .map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Primary Supplier *</label>
                  <select
                    required
                    className="w-full px-4 py-2 rounded-lg border-2 border-orange-200 dark:border-orange-900/30 dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={editing.preferred_supplier_id || ""}
                    onChange={(e) => setEditing({ ...editing, preferred_supplier_id: e.target.value })}
                  >
                    <option value="">Select Primary Supplier</option>
                    {availableSuppliers.map(sup => (
                      <option key={sup.id} value={sup.id}>{sup.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                    HSN Code
                    <span className="ml-2 text-xs font-normal text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">Smart Search</span>
                  </label>
                  <SmartHSNInput
                    value={editing.hsn_code || ""}
                    gstValue={editing.tax}
                    productName={editing.name}
                    mainCategory={categories.find(c => String(c.id) === String(selectedEditMainCat || categories.find(curr => String(curr.id) === String(editing.category_id))?.parent))?.name || ""}
                    subCategory={categories.find(c => String(c.id) === String(editing.category_id))?.name || ""}
                    onHSNChange={(hsn, gst) => setEditing(prev => ({ ...prev, hsn_code: hsn, tax: gst }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Barcode</label>
                  <BarcodeSection
                    value={editing.barcode}
                    onChange={(val) => setEditing(prev => ({ ...prev, barcode: val }))}
                    productName={editing.name}
                    category={categories.find(c => String(c.id) === String(editing.category_id))?.name || ""}
                    price={editing.sellingPrice || editing.unit_price}
                    isEditing={true}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Unit</label>
                    <select
                      className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editing.unit || "Piece"}
                      onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                    >
                      {availableUnits.map(u => (
                        <option key={u.id} value={u.symbol}>{u.name} ({u.symbol})</option>
                      ))}
                      {/* Fallback */}
                      {availableUnits.length === 0 && ["Piece", "Kg", "Litre", "Box", "Meter"].map(u => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">GST %</label>
                    <select
                      className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editing.tax}
                      onChange={(e) => setEditing({ ...editing, tax: Number(e.target.value) })}
                    >
                      <option value={0}>0%</option>
                      {availableTaxRates.map(tax => (
                        <option key={tax.id} value={Number(tax.gst_percentage)}>
                          {tax.name}
                        </option>
                      ))}
                      {/* Fallbacks if API fails */}
                      {availableTaxRates.length === 0 && [5, 12, 18, 28].map(rate => (
                        <option key={rate} value={rate}>{rate}%</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-span-2 space-y-3 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700 mt-4">
                  <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">Product Tags</h3>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={editing.is_popular}
                        onChange={(e) => setEditing({ ...editing, is_popular: e.target.checked })}
                      />
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-400 group-hover:text-green-600 transition-colors">Most Popular</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={editing.is_recommended}
                        onChange={(e) => setEditing({ ...editing, is_recommended: e.target.checked })}
                      />
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-400 group-hover:text-blue-600 transition-colors">Recommended</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        checked={editing.is_special}
                        onChange={(e) => setEditing({ ...editing, is_special: e.target.checked })}
                      />
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-400 group-hover:text-purple-600 transition-colors">Special Offer</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Current Stock (Read-Only)</label>
                  <input
                    type="number"
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-100 dark:border-dark-border dark:bg-dark-card dark:text-gray-400 cursor-not-allowed"
                    value={editing.stock}
                    readOnly
                    title="Stock can only be updated via Stock Inward/Sales"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Product Type *</label>
                  <select
                    required
                    className="w-full px-4 py-2 rounded-lg border-2 border-purple-200 dark:border-purple-900/30 dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={editing.product_type || (editing.has_variants ? "variant" : "fixed")}
                    onChange={(e) => {
                      const type = e.target.value;
                      setEditing(prev => {
                        const parentCategory = categories.find(c => String(c.id) === String(prev.category_id))?.name || "";
                        const mainBarcode = prev.barcode || generateBarcodeValue(prev.name, parentCategory, null, 'GIO_PRD', 'CODE128');
                        const updated = {
                          ...prev,
                          barcode: mainBarcode,
                          product_type: type,
                          unit: type === 'weight_based' ? 'Kg' : prev.unit,
                        };
                        if (type === 'variant' && (!prev.variants || prev.variants.length === 0)) {
                          const variantBarcode = generateBarcodeValue(prev.name, parentCategory, "V1", 'GIO_PRD', 'CODE128');
                          updated.variants = [{
                            tempId: Date.now(),
                            name: "",
                            barcode: variantBarcode,
                            purchase_price: "",
                            unit_price: "",
                            stock: 0,
                            minimum_stock: 5,
                            is_active: true
                          }];
                        } else if (type !== 'variant') {
                          updated.variants = [];
                        }
                        return updated;
                      });

                    }}
                  >
                    <option value="fixed">Fixed Product</option>
                    <option value="variant">Variant Product</option>
                    <option value="weight_based">Weight-Based</option>
                  </select>
                </div>

                {!(editing.product_type === 'variant' || editing.has_variants) && (
                  <>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Purchase Price</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={editing.purchasePrice}
                        onChange={(e) => setEditing({ ...editing, purchasePrice: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Selling Price</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={editing.sellingPrice}
                        onChange={(e) => setEditing({ ...editing, sellingPrice: Number(e.target.value) })}
                      />
                    </div>
                  </>
                )}

                {(editing.product_type === 'variant' || editing.has_variants) && (
                  <div className="col-span-2 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-6 mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">Product Variants</h3>
                      <button
                        type="button"
                        onClick={() => addVariantToState(true)}
                        className="px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-sm font-bold hover:bg-blue-100 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Variant
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(editing.variants || []).map((v, idx) => (
                        <div key={v.id || v.tempId || idx} className="p-4 rounded-xl border border-blue-100 dark:border-blue-900/30 bg-blue-50/30 dark:bg-blue-900/10 relative group">
                          <button
                            type="button"
                            onClick={() => removeVariantFromState(idx, true)}
                            className="absolute -top-2 -right-2 p-1.5 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-1">
                              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Variant Name</label>
                              <input
                                type="text"
                                value={v.name}
                                onChange={(e) => updateVariantInState(idx, 'name', e.target.value, true)}
                                placeholder="e.g. Small / Red"
                                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase">Barcode</label>
                                {v.barcode && (
                                  <div className="flex gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        navigator.clipboard.writeText(v.barcode);
                                        addNotification("success", "Copied", "Variant barcode copied!");
                                      }}
                                      className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      Copy
                                    </button>
                                    <span className="text-gray-300">|</span>
                                    <button
                                      type="button"
                                      onClick={() => downloadBarcodeAsPNG(v.barcode, editing.name, v.name || `Var #${idx+1}`, v.unit_price)}
                                      className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      PNG
                                    </button>
                                    <span className="text-gray-300">|</span>
                                    <button
                                      type="button"
                                      onClick={() => printBarcodeLabel(v.barcode, editing.name, v.name || `Var #${idx+1}`, v.unit_price)}
                                      className="text-[9px] text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      Print
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1.5">
                                <input
                                  type="text"
                                  value={v.barcode}
                                  onChange={(e) => updateVariantInState(idx, 'barcode', e.target.value, true)}
                                  className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
                                  placeholder="Auto or Enter"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const val = generateBarcodeValue(editing.name, categories.find(c => String(c.id) === String(editing.category_id))?.name, v.name || `V${idx+1}`, 'GIO_PRD', 'CODE128');
                                    updateVariantInState(idx, 'barcode', val, true);
                                  }}
                                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-bold whitespace-nowrap hover:bg-blue-200"
                                >
                                  Gen
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Purchase</label>
                                <input
                                  type="number"
                                  value={v.purchase_price}
                                  onChange={(e) => updateVariantInState(idx, 'purchase_price', e.target.value, true)}
                                  className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Selling</label>
                                <input
                                  type="number"
                                  value={v.unit_price}
                                  onChange={(e) => updateVariantInState(idx, 'unit_price', e.target.value, true)}
                                  className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Update Product Image</label>

                  {editing.image && (
                    <div className="mb-4 flex items-center gap-4">
                      <div className="w-24 h-24 rounded-2xl border-2 border-gray-100 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-900/50 shadow-md relative group">
                        {editing.image ? (
                          <img
                            src={
                              typeof editing.image === 'string'
                                ? resolveMediaUrl(editing.image)
                                : URL.createObjectURL(editing.image)
                            }
                            alt="Product Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl opacity-50 bg-gray-100 dark:bg-gray-800">
                            🥘
                          </div>
                        )}
                        {/* Fallback overlay if image fails or is being uploaded */}
                        {typeof editing.image === 'object' && (
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <Upload className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {typeof editing.image === 'string' ? 'Current Image' : 'New Image Selected'}
                        </span>
                        {typeof editing.image === 'object' && (
                          <span className="text-xs text-green-600 font-medium">Ready to upload</span>
                        )}
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      setEditImageError("");
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 1024 * 1024) {
                          addNotification("error", "File too large", "Please upload an image smaller than 1MB.");
                          setEditImageError("File too large. Please upload an image smaller than 1MB.");
                          e.target.value = null;
                          return;
                        }
                        setEditing({ ...editing, image: file });
                      }
                    }}
                    className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${editImageError ? 'border border-red-500 rounded-lg p-1' : ''}`}
                  />
                  {editImageError ? (
                    <p className="text-xs text-red-500 mt-2 font-medium">{editImageError}</p>
                  ) : (
                    <p className="text-[10px] text-gray-500 mt-1 uppercase font-bold tracking-wider italic">Max size: 1MB (For better performance)</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                {canManageInventory && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => saveProduct(editing)}
                    className="flex-1 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors"
                  >
                    Save Changes
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditing(null);
                    setEditImageError("");
                  }}
                  className="flex-1 py-3 rounded-lg border-2 border-gray-300 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )
      }

      {/* Stock Alerts Modal */}
      {showAlertsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                Low Stock Alerts
              </h2>
              <button onClick={() => setShowAlertsModal(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The following {alertItems.length} items are currently at or below their reorder levels. Please contact your suppliers.
            </p>

            <div className="flex-1 overflow-y-auto pr-2 space-y-3 mb-6">
              {alertItems.map((item) => (
                <div key={item.id} className="p-4 rounded-lg border border-yellow-200 dark:border-yellow-900/30 bg-yellow-50/50 dark:bg-yellow-900/10 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900 dark:text-white uppercase">{item.stock_status}</span>
                      <p className="font-bold text-gray-900 dark:text-white">{item.name}</p>
                    </div>
                    <div className="flex gap-4 mt-1">
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium font-bold">Stock: {item.stock}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Reorder Level: {item.reorder_level}</p>
                    </div>
                    {item.preferred_supplier && (
                      <p className={`text-xs mt-1 font-medium italic ${item.supplier_id ? 'text-blue-600 dark:text-blue-400' : 'text-red-500 font-bold'}`}>
                        Supplier: {item.preferred_supplier}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {item.notified ? (
                      <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-lg text-xs font-bold flex items-center gap-1">
                        <CheckCheck className="w-3 h-3" />
                        Message Sent
                      </span>
                    ) : item.supplier_id ? (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-lg text-xs font-bold">
                        Pending
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-lg text-xs font-bold">
                        No Supplier
                      </span>
                    )}
                    <button
                      onClick={() => {
                        const product = products.find(p => p.id === item.id);
                        if (product) setEditing(product);
                        setShowAlertsModal(false);
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  handleExportProducts();
                  setShowAlertsModal(false);
                }}
                className="flex-1 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export List
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddStockModal && selectedStockProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-dark-card rounded-2xl p-6 max-w-md w-full shadow-2xl border border-blue-100 dark:border-dark-border"
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                Add Stock
              </h2>
              <button 
                onClick={() => {
                  setShowAddStockModal(false);
                  setSelectedStockProduct(null);
                  setSelectedStockVariant(null);
                }} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Product Name</label>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {selectedStockProduct.name}
                  {selectedStockVariant && (
                    <span className="ml-1.5 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-xs font-black">
                      {selectedStockVariant.name}
                    </span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Current Stock</label>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    {selectedStockVariant ? selectedStockVariant.stock : selectedStockProduct.stock} {selectedStockProduct.unit}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Min Threshold</label>
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    {selectedStockVariant ? (selectedStockVariant.minimum_stock || 5) : (selectedStockProduct.minimum_stock || 5)} {selectedStockProduct.unit}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quantity to Add *</label>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="any"
                  placeholder={`Enter quantity in ${selectedStockProduct.unit || 'units'}`}
                  value={stockQtyToAdd}
                  onChange={(e) => setStockQtyToAdd(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Adjustment Reason / Notes</label>
                <input
                  type="text"
                  placeholder="e.g. Restock from supplier, batch correction"
                  value={stockReason}
                  onChange={(e) => setStockReason(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-blue-200 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAdjustStockSubmit}
                className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold rounded-xl shadow-lg transition-colors flex items-center justify-center gap-2"
              >
                Add Stock
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowAddStockModal(false);
                  setSelectedStockProduct(null);
                  setSelectedStockVariant(null);
                }}
                className="flex-1 py-3 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Custom Alert Modal */}
      {customAlert.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            className="bg-white dark:bg-dark-card border border-blue-100 dark:border-dark-border rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4 text-center"
          >
            <div className={`mx-auto w-12 h-12 rounded-2xl flex items-center justify-center ${customAlert.type === 'error' ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'}`}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{customAlert.title}</h3>
              <p className="text-sm text-gray-500 mt-2 whitespace-pre-line">{customAlert.message}</p>
            </div>
            <button
              onClick={() => setCustomAlert(prev => ({ ...prev, show: false }))}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-md transition-all text-sm"
            >
              OK
            </button>
          </motion.div>
        </div>
      )}
    </div >
  );
}